import {
  eventEmitterDefaultLogger,
  EventEmitterWithLogger,
} from '@surglogs/eventemitter'

import { captureException } from '@surglogs-app/error-reporting'
import { ClinicListApiEntity } from '@surglogs-app/network'
import {
  type IClinicContextStore,
  ClinicContextStoreEvent,
  type ClinicContextStoreEventData,
  ClinicContextStoreListener,
} from '@surglogs-app/types'

export class ClinicContextStore<
    TData extends ClinicContextStoreEventData | number,
  >
  extends EventEmitterWithLogger<ClinicContextStoreEvent>
  implements IClinicContextStore
{
  protected logger = eventEmitterDefaultLogger

  protected logPrefix = 'ClinicContextStore'

  private data: ClinicContextStoreEventData | null = null

  private clinicId: number | null = null

  private clinicIdDataCurrentlyLoading: number | null = null

  private fetcher:
    | ((clinicId: number, token: string) => Promise<ClinicListApiEntity>)
    | null = null

  constructor(
    fetcher: (clinicId: number, token: string) => Promise<ClinicListApiEntity>,
    clinicId: number | null,
  ) {
    super()
    this.clinicId = clinicId
    this.fetcher = fetcher
  }

  getData() {
    return this.data
  }

  getClinicId() {
    return this.clinicId
  }

  onChange(listener: ClinicContextStoreListener<TData | null>): () => void {
    this.on(ClinicContextStoreEvent.CLINIC_UPDATED, listener)
    this.on(ClinicContextStoreEvent.CLINIC_ID_UPDATED, listener)
    return () => {
      this.off(ClinicContextStoreEvent.CLINIC_UPDATED, listener)
      this.off(ClinicContextStoreEvent.CLINIC_ID_UPDATED, listener)
    }
  }

  on(
    eventName: ClinicContextStoreEvent,
    fn: ClinicContextStoreListener<TData | null>,
  ) {
    return super.on(eventName, fn)
  }

  changeClinic(clinicId: number, token: string): this {
    if (
      this.data?.id !== clinicId &&
      this.clinicIdDataCurrentlyLoading !== clinicId
    ) {
      this.clinicId = clinicId
      this.clinicIdDataCurrentlyLoading = clinicId
      this.fetcher?.(clinicId, token)
        ?.then((loadedClinicData) => {
          this.setData(loadedClinicData)
          if (loadedClinicData?.id !== clinicId) {
            this.setClinicId(loadedClinicData?.id)
          }
        })
        .catch((e) => {
          captureException(e)
        })
        .finally(() => {
          this.clinicIdDataCurrentlyLoading = null
        })
    }

    return this
  }

  refetchClinicData(token: string): this {
    if (this.clinicId) {
      this.fetcher?.(this.clinicId, token)
        ?.then((d) => this.setData(d))
        .catch((e) => {
          captureException(e)
        })
    }
    return this
  }

  clear() {
    this.data = null
    this.clinicId = null
    this.clinicIdDataCurrentlyLoading = null
    this.emit(ClinicContextStoreEvent.CLINIC_UPDATED, this.data)

    return this
  }

  setData(data: ClinicContextStoreEventData) {
    this.data = data
    this.emit(ClinicContextStoreEvent.CLINIC_UPDATED, this.data)

    return this
  }

  setClinicId(clinicId: number) {
    this.clinicId = clinicId
    this.emit(ClinicContextStoreEvent.CLINIC_ID_UPDATED, this.clinicId)

    return this
  }
}
